import { observer } from "mobx-react-lite";
import { Suspense } from "react";

import {
  CenteredLargeSpinner,
  MessageBar,
  MessageBarType,
  Stack,
  Tile
} from "@bps/fluent-ui";
import {
  EncounterType,
  PermanentClinicalTab
} from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { usePatientRecordScreenContext } from "@modules/clinical/screens/context/PatientRecordScreenContext.ts";
import { DischargeStatus } from "@shared-types/clinical/discharge-status.enum.ts";

import { EncounterDetails } from "./EncounterDetails.tsx";
import { PatientRecordContent } from "./PatientRecordContent.tsx";
import { EncounterForm } from "./PatientRecordLazy.tsx";

export const PatientRecordNotesArea = observer(() => {
  const { isViewOnly, isViewOnlyOrDischarged, clinicalRecord } =
    usePatientRecordScreenContext();

  if (!clinicalRecord) return null;

  const isRecordUpdate =
    clinicalRecord?.openEncounter?.type === EncounterType.RecordUpdate;

  let content: JSX.Element | null = null;

  const dischargeItems = clinicalRecord.clinicalData?.discharge?.dataItems;

  //check the incomplete discharge for the current logged in business role
  const businessRole = clinicalRecord?.openEncounter?.businessRole;

  const { getLatestActiveDischargeClinicalDataItem } =
    usePatientRecordScreenContext();

  const latestDischargeItem = getLatestActiveDischargeClinicalDataItem(
    businessRole,
    dischargeItems
  );

  const hasReversedDischarges = dischargeItems?.some(
    x => x.dischargeStatus === DischargeStatus.Reversed
  );

  const doesNotHaveExistingDischarge =
    latestDischargeItem &&
    latestDischargeItem.dischargeStatus !== DischargeStatus.Completed;

  if (
    clinicalRecord.patientRecordTab.activeTab &&
    clinicalRecord.patientRecordTab.activeTab.type !==
      PermanentClinicalTab.TodaysNotes
  ) {
    content = (
      <PatientRecordContent
        key={
          clinicalRecord.patientRecordTab.activeTab.type +
          clinicalRecord.patientRecordTab.activeTab.contextId
        }
        clinicalRecord={clinicalRecord}
      />
    );
  } else if (
    isViewOnlyOrDischarged ||
    (!isViewOnly &&
      !isRecordUpdate &&
      doesNotHaveExistingDischarge &&
      !latestDischargeItem.id &&
      dischargeItems)
  ) {
    const encounter = clinicalRecord?.openEncounter;

    if (encounter) {
      content = <EncounterDetails clinicalRecord={clinicalRecord} />;
    }
  } else {
    content = (
      <Suspense fallback={<CenteredLargeSpinner />}>
        <EncounterForm />
      </Suspense>
    );
  }

  return (
    <Stack
      styles={{
        root: {
          overflowY: "hidden",
          height: "100%"
        }
      }}
      grow
    >
      {!isViewOnly && !isRecordUpdate && hasReversedDischarges && (
        <MessageBar messageBarType={MessageBarType.warning}>
          <Stack horizontal verticalAlign="center">
            The discharge has been reversed and the condition will be linked to
            this consult
          </Stack>
        </MessageBar>
      )}
      <Tile
        styles={{
          root: { flexGrow: 1, overflowY: "hidden", padding: 16 },
          content: {
            padding: 0,
            flexGrow: 1,
            display: "flex",
            flexDirection: "column"
          }
        }}
      >
        {content}
      </Tile>
    </Stack>
  );
});
